import { queryClient } from '@/libs/query-client';

export const baseURL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000';

const clearAuthAndRedirect = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  queryClient.setQueryData(['auth'], { isAuthenticated: false });
  window.location.href = '/signin';
};

export class APIError extends Error {
  constructor(
    message: string,
    public status?: number,
    public data?: unknown,
  ) {
    super(message);
    this.name = 'APIError';
  }
}

async function fetchWithAuth(input: string, init?: RequestInit): Promise<Response> {
  const token = localStorage.getItem('accessToken');
  const headers = new Headers(init?.headers);

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  headers.set('Content-Type', 'application/json');

  const response = await fetch(`${baseURL}${input}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    if (response.status === 401) {
      let newAccessToken: string;
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) throw new Error('No refresh token');

        const refreshResponse = await fetch(`${baseURL}/api/token/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh: refreshToken }),
        });

        if (!refreshResponse.ok) throw new Error('Token refresh failed');

        const { access } = (await refreshResponse.json()) as { access?: string };
        if (!access) throw new Error('Token refresh failed');
        newAccessToken = access;
        localStorage.setItem('accessToken', newAccessToken);
      } catch {
        clearAuthAndRedirect();
        throw new APIError('Authentication failed', 401);
      }

      headers.set('Authorization', `Bearer ${newAccessToken}`);
      const retryResponse = await fetch(`${baseURL}${input}`, {
        ...init,
        headers,
      });

      if (retryResponse.status === 401) {
        clearAuthAndRedirect();
        throw new APIError('Authentication failed', 401);
      }
      if (!retryResponse.ok) {
        const retryErrorData = await retryResponse.json().catch(() => null);
        throw new APIError('API request failed', retryResponse.status, retryErrorData);
      }
      return retryResponse;
    }

    const errorData = await response.json().catch(() => null);
    throw new APIError('API request failed', response.status, errorData);
  }

  return response;
}

export const api = {
  async get<T>(path: string): Promise<T> {
    const response = await fetchWithAuth(path);
    return response.json();
  },

  async post<T>(path: string, data: unknown): Promise<T> {
    const response = await fetchWithAuth(path, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (response.status === 204) return undefined as T;
    return response.json();
  },

  async put<T>(path: string, data: unknown): Promise<T> {
    const response = await fetchWithAuth(path, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (response.status === 204) return undefined as T;
    return response.json();
  },

  async patch<T>(path: string, data: unknown): Promise<T> {
    const response = await fetchWithAuth(path, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    if (response.status === 204) return undefined as T;
    return response.json();
  },

  async delete(path: string): Promise<void> {
    await fetchWithAuth(path, {
      method: 'DELETE',
    });
  },
};
